
import React, { useState, useRef, useEffect } from 'react';
import { Coffee } from 'lucide-react';
import { ComparisonCanvas } from './components/ComparisonCanvas';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { Modal } from './components/Modal';
import { ComparisonState, Theme, Language } from './types';
import { THEME_PRESETS, THEME_BUTTON_CONFIG } from './themes';
import { INITIAL_STATE } from './constants';
import { translations } from './translations';
import { trackPageView, trackEvent } from './utils/analytics';
import { generateDynamicCode } from './utils/codeGenerator';
import { serializeStateToUrl, deserializeStateFromUrl } from './utils/urlState';

declare const html2canvas: any;

const App: React.FC = () => {
  const [state, setState] = useState<ComparisonState>(INITIAL_STATE);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Modal State
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'code' | 'link';
    content?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'link'
  });

  // Load state from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('before') || params.has('after') || params.has('c')) {
      const decodedState = deserializeStateFromUrl(params, INITIAL_STATE);
      setState(decodedState);
      setIsViewOnly(true);
    }
    trackPageView(window.location.pathname);
  }, []);

  const updateState = <K extends keyof ComparisonState>(key: K, value: ComparisonState[K]) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  const handleLanguageChange = (newLang: Language) => {
    const prevLang = state.language;
    const oldT = translations[prevLang];
    const newT = translations[newLang];

    setState(prev => {
      const newState = { ...prev, language: newLang };
      if (prev.title === oldT.default_title) newState.title = newT.default_title;
      if (prev.beforeLabel === oldT.default_before_label) newState.beforeLabel = newT.default_before_label;
      if (prev.afterLabel === oldT.default_after_label) newState.afterLabel = newT.default_after_label;
      if (prev.beforeDesc === oldT.default_before_desc) newState.beforeDesc = newT.default_before_desc;
      if (prev.afterDesc === oldT.default_after_desc) newState.afterDesc = newT.default_after_desc;
      return newState;
    });
    
    trackEvent('change_language', 'engagement', newLang);
  };

  const toSnakeCase = (str: string) => {
    return str
      .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      .replace(/^_/, '')
      .replace(/-/g, '_');
  };

  const applyTheme = (theme: Theme) => {
    const preset = THEME_PRESETS[theme];
    if (preset) {
      setState(prev => ({ ...prev, ...preset }));
      // Envoi du nom du thème en snake_case
      trackEvent('select_theme', 'design', toSnakeCase(theme));
    }
  };

  const handleExport = async () => {
    if (state.exportMode === 'dynamic') {
      const code = generateDynamicCode(state);
      await navigator.clipboard.writeText(code);
      trackEvent('copy_code_success', 'conversion', state.theme);
      setModal({
        isOpen: true,
        title: 'Snippet Copié !',
        message: 'Le code HTML/CSS interactif est prêt à être collé sur votre site web.',
        type: 'code',
        content: code
      });
      return;
    }

    if (state.exportMode === 'link') {
      const url = serializeStateToUrl(state);
      await navigator.clipboard.writeText(url);
      trackEvent('copy_link_success', 'conversion', state.theme);
      setModal({
        isOpen: true,
        title: 'Lien Copié !',
        message: 'Toute votre configuration est stockée dans ce lien. Partagez-le librement.',
        type: 'link',
        content: url
      });
      return;
    }

    if (!exportRef.current) return;
    setIsExporting(true);
    trackEvent('download_visual_start', 'conversion', state.theme);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const canvas = await html2canvas(exportRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: state.bgColor,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `perspective-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      trackEvent('download_visual_success', 'conversion', state.theme);
    } catch (err) {
      console.error(err);
      trackEvent('download_visual_error', 'error', err instanceof Error ? err.message : 'unknown');
    } finally {
      setIsExporting(false);
    }
  };

  const t = translations[state.language];

  const FooterCredit = () => (
    <div className="mt-8 mb-4 flex flex-col items-center gap-6">
      <div className="flex items-center gap-2 text-slate-600 text-[10px] font-bold uppercase tracking-[0.3em]">
        <span>Powered by</span>
        <a 
          href="https://www.upgile.fr/" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-indigo-400 hover:text-indigo-300 transition-colors underline underline-offset-4 decoration-indigo-400/20 hover:decoration-indigo-400"
        >
          Upgile
        </a>
      </div>
      
      <a 
        href="https://www.buymeacoffee.com/pierrealain" 
        target="_blank" 
        rel="noopener noreferrer"
        onClick={() => trackEvent('click_buy_me_coffee', 'engagement', state.language)}
        className="group flex items-center gap-3 px-8 py-4 bg-[#FFDD00] hover:bg-[#FFEA00] text-slate-900 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all hover:scale-105 active:scale-95 shadow-[0_10px_30px_rgba(255,221,0,0.15)] hover:shadow-[0_15px_40px_rgba(255,221,0,0.3)]"
      >
        <Coffee className="w-4 h-4 transition-transform group-hover:rotate-12" />
        {t.buy_me_coffee}
      </a>
    </div>
  );

  if (isViewOnly) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-950 text-slate-200">
        <Header language={state.language} onLanguageChange={handleLanguageChange} />
        
        <main className="flex-1 flex flex-col items-center py-12 px-4 overflow-x-hidden overflow-y-auto custom-scrollbar">
           <div className="w-full max-w-[1200px] flex flex-col items-center">
             <ComparisonCanvas 
              state={state} 
              onSplitChange={(pos) => updateState('splitPosition', pos)}
              onLabelMove={() => {}} 
              onLabelResize={() => {}} 
              exportRef={exportRef}
             />
           </div>
        </main>

        <footer className="w-full p-12 border-t border-white/5 bg-slate-900/40 backdrop-blur-3xl flex flex-col items-center gap-6">
          <p className="text-slate-400 text-sm font-medium tracking-wide text-center">
            {state.language === 'fr' ? 'Vous aimez ce visuel ?' : 'Do you like this visual?'}
          </p>
          <button 
            onClick={() => {
              trackEvent('click_create_mine', 'engagement', toSnakeCase(state.theme));
              window.location.href = window.location.pathname;
            }}
            className="px-12 py-6 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 hover:scale-105 rounded-full font-black text-xl tracking-widest transition-all shadow-[0_20px_50px_rgba(79,70,229,0.4)] active:scale-95 text-white uppercase"
          >
            {t.create_yours}
          </button>
          <FooterCredit />
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 flex flex-col items-center max-w-[1800px] mx-auto bg-slate-950 text-slate-200">
      <Header language={state.language} onLanguageChange={handleLanguageChange} />

      <ThemeSwitcher state={state} applyTheme={applyTheme} themeConfig={THEME_BUTTON_CONFIG} />

      <div className="w-full grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-12 items-start">
        <Sidebar 
          state={state}
          updateState={updateState}
          applyTheme={applyTheme}
          handleExport={handleExport}
          isExporting={isExporting}
        />

        <div className="flex flex-col gap-8 items-center">
           <div className="w-full max-w-[1200px]">
             <ComparisonCanvas 
              state={state} 
              onSplitChange={(pos) => updateState('splitPosition', pos)}
              onLabelMove={(target, pos) => updateState(target === 'before' ? 'beforeLabelPos' : 'afterLabelPos', pos)}
              onLabelResize={(target, size) => updateState(target === 'before' ? 'beforeLabelSize' : 'afterLabelSize', size)}
              exportRef={exportRef}
             />
           </div>
           <div className="flex flex-col items-center gap-2">
             <p className="text-slate-600 text-[10px] font-bold uppercase tracking-[0.3em] text-center max-w-lg">
               {t.tip}
             </p>
             <FooterCredit />
           </div>
        </div>
      </div>

      <Modal 
        isOpen={modal.isOpen}
        onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        content={modal.content}
      />
    </div>
  );
};

export default App;
